import polygonClipping from 'polygon-clipping'
import type { GeoArea, GeoPt } from '../types'
import { capRingsPerPolygon } from './capRings'
import { areasToClipperPolygons, clipperResultToGeo } from './clipperPolygons'
import { clusterRings, reverseMargin } from './clusterRings'
import { containingEllipse, ellipseRing, expandEllipse } from './containingEllipse'

/** The app samples its oval at 64 points; `ground_combine`'s `_ellipseResolution`. */
export const ELLIPSE_RESOLUTION = 64
/** The fit stops once the worst point sits within this many metres of the ellipse. */
export const ELLIPSE_TOLERANCE_M = 100
export const ELLIPSE_MAX_ITERATIONS = 100

/**
 * The `reverse` wash: one oval per cluster of nearby hunt parcels, with the
 * parcels themselves cut out, the app's `calculateReverseAreas`.
 *
 * Parcels are clustered by margin-expanded bounding box first (see
 * `clusterRings`); each cluster then gets ONE minimum-volume enclosing ellipse
 * over the hull of its rings, grown by the same clamped margin, sampled as a
 * polygon and differenced against the cluster's own polygons. Only the
 * cluster's own: a neighbouring cluster's parcel that happens to fall inside
 * this oval is washed, exactly as on mobile, so the two platforms agree.
 *
 * A cluster whose fit or clip throws is skipped rather than failing the map;
 * the outline layers still draw it.
 */
export function reverseWashPolygons(huntAreas: readonly GeoArea[]): GeoPt[][][] {
  const polys = areasToClipperPolygons(huntAreas)
  if (polys.length === 0) return []
  const clusters = clusterRings(polys.map((poly) => poly[0]))
  const out: GeoPt[][][] = []
  for (const cluster of clusters) {
    try {
      const fitted = containingEllipse(cluster.hull, ELLIPSE_TOLERANCE_M, ELLIPSE_MAX_ITERATIONS)
      const oval = expandEllipse(fitted, reverseMargin(Math.max(fitted.a, fitted.b)))
      const ring = ellipseRing(oval, ELLIPSE_RESOLUTION)
      const subject = [[...ring, ring[0]]]
      const clips = cluster.ringIndices.map((i) => polys[i])
      const diff = capRingsPerPolygon(polygonClipping.difference(subject, ...clips))
      out.push(...clipperResultToGeo(diff))
    } catch {
      // Skipped on purpose; see the doc comment.
    }
  }
  return out
}
